/**
 * Oppgaver (#293): the page's pure rules, kept here so the grid, the list, the counts and the report
 * all read one membership. A task counts once in a period whatever its length; done means done or a
 * manager's exception; a multi-day task is late only after its frist, never because its start passed.
 */
import type { ApiTask, TaskWrite } from '@/api/tasks';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { DN, MONTHS, dayMonth } from '@/ui/format';
import { addDays, dow, mondayOf } from '@timefly/core';

export type View = 'day' | 'week' | 'two' | 'month';
export type Filter = 'all' | 'follow' | 'done';
export type Shown = 'done' | 'exception' | 'late' | 'unassigned' | 'open';

/** The first and last day a view shows around a date. */
export function periodOf(view: View, date: string): { from: string; to: string } {
  if (view === 'day') return { from: date, to: date };
  if (view === 'month') {
    const first = `${date.slice(0, 7)}-01`;
    const last = addDays(`${addDays(first, 32).slice(0, 7)}-01`, -1);
    return { from: first, to: last };
  }
  const from = mondayOf(date);
  return { from, to: addDays(from, view === 'two' ? 13 : 6) };
}

/** Every day from `from` to `to`, both included. */
export function daysOf(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/** One step of the view forward (1) or back (-1). */
export function step(view: View, date: string, n: 1 | -1): string {
  if (view === 'day') return addDays(date, n);
  if (view === 'month') return `${addDays(`${date.slice(0, 7)}-15`, n * 30).slice(0, 7)}-01`;
  return addDays(date, n * (view === 'two' ? 14 : 7));
}

/** What a task is now, as the page says it; colour is never the only signal. */
export function shownOf(t: ApiTask, today: string): Shown {
  if (t.status === 'done') return 'done';
  if (t.status === 'exception') return 'exception';
  if (t.due_date < today) return 'late';
  if (t.assignee_id == null) return 'unassigned';
  return 'open';
}

export const isDone = (t: ApiTask) => t.status !== 'open';
/** Needs the manager: open and past its frist, or open with nobody accountable. */
export const needsFollowUp = (t: ApiTask, today: string) =>
  t.status === 'open' && (t.due_date < today || t.assignee_id == null);

export const SHOWN_LABEL: Record<Shown, string> = {
  done: 'Utført',
  exception: 'Unntak',
  late: 'Frist passert',
  unassigned: 'Uten ansvarlig',
  open: 'Åpen',
};

/** Search reads the title, the person and the type. */
export function matches(t: ApiTask, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [t.title, t.assignee_name ?? '', t.type?.name ?? ''].some((x) => x.toLowerCase().includes(s));
}

/** The tasks the page shows for a search and a status filter; the counts and the report read the same. */
export function visible(tasks: ApiTask[], q: string, filter: Filter, today: string): ApiTask[] {
  return tasks.filter(
    (t) => matches(t, q) && (filter === 'all' || (filter === 'done' ? isDone(t) : needsFollowUp(t, today))),
  );
}

export function counts(tasks: ApiTask[], q: string, today: string) {
  const found = tasks.filter((t) => matches(t, q));
  return {
    all: found.length,
    follow: found.filter((t) => needsFollowUp(t, today)).length,
    done: found.filter(isDone).length,
  };
}

/** The tasks a person/day cell holds: the day falls in the task's window. */
export const onDay = (t: ApiTask, day: string) => t.start_date <= day && t.due_date >= day;

/** The task's time line: «Kl. 09:15», «Frist 23. okt.» for a longer window, else by the end of the day. */
export function whenOf(t: Pick<ApiTask, 'start_time' | 'start_date' | 'due_date'>): string {
  const parts: string[] = [];
  if (t.start_time) parts.push(T('Kl. {time}', { time: t.start_time }));
  if (t.due_date !== t.start_date) parts.push(T('Frist {date}', { date: dayMonth(t.due_date) }));
  else if (!t.start_time) parts.push(T('Innen dagen er omme'));
  return parts.join(' · ');
}

/** Minutes as «1,5 t» or «45 min»: an estimate, said as one. */
export function estimateOf(min: number | null): string {
  if (min == null) return '';
  if (min < 60) return T('{n} min estimert', { n: min });
  return T('{n} t estimert', { n: (min / 60).toLocaleString('nb-NO', { maximumFractionDigits: 1 }) });
}

export const dayHead = (d: string) => `${DN[dow(d)]}. ${Number(d.slice(8, 10))}.`;
export const rangeText = (from: string, to: string) =>
  from === to
    ? `${dayMonth(from)} ${from.slice(0, 4)}`
    : `${dayMonth(from)} – ${dayMonth(to)} ${to.slice(0, 4)}`;
export const monthText = (d: string) => `${MONTHS[Number(d.slice(5, 7)) - 1] ?? ''} ${d.slice(0, 4)}`;

/* ── the form ─────────────────────────────────────────────────────────── */

export interface Draft {
  title: string;
  type_id: number | null;
  start_date: string;
  due_date: string;
  start_time: string;
  /** hours as typed, «1,5»; empty is no estimate */
  estimate: string;
  requirement: '' | 'comment' | 'measurement';
  /** «later», «person» or «shift» */
  mode: 'later' | 'person' | 'shift';
  assignee_id: number | null;
  shift_id: number | null;
  repeat: '' | 'daily' | 'weekly' | 'fortnightly';
  until: string;
  save_as_type: boolean;
  icon: string | null;
  color: string | null;
}

export const FIELD_ORDER = [
  'title',
  'start_date',
  'due_date',
  'start_time',
  'estimate',
  'assignee_id',
  'shift_id',
  'until',
] as const;
export type Field = (typeof FIELD_ORDER)[number];
const isDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) &&
  new Date(`${v}T00:00:00Z`).toISOString().startsWith(v);
const isTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

/** Hours typed with a comma or a point to whole minutes; null for empty, NaN for anything not a finite non-negative number. */
export function minutesOf(hours: string): number | null {
  const s = hours.trim().replace(',', '.');
  if (!s) return null;
  if (!/^\d+(\.\d+)?$/.test(s)) return Number.NaN;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 60) : Number.NaN;
}
export const hoursOf = (min: number | null) => (min == null ? '' : String(min / 60).replace('.', ','));

/** Every problem in the draft, in the form's order, so the first can take focus. */
export function validate(d: Draft): Partial<Record<Field, string>> {
  const e: Partial<Record<Field, string>> = {};
  if (!d.title.trim()) e.title = T('Skriv hva som skal gjøres.');
  else if (d.title.trim().length > 200) e.title = T('Tittelen kan være høyst 200 tegn.');
  if (!isDate(d.start_date)) e.start_date = T('Velg en gyldig startdato.');
  if (d.due_date && !isDate(d.due_date)) e.due_date = T('Velg en gyldig frist.');
  else if (d.due_date && isDate(d.start_date) && d.due_date < d.start_date)
    e.due_date = T('Fristen kan ikke være før startdatoen.');
  if (d.start_time && !isTime(d.start_time))
    e.start_time = T('Skriv klokkeslett som TT:MM, eller la feltet stå tomt.');
  const m = minutesOf(d.estimate);
  if (m != null && Number.isNaN(m)) e.estimate = T('Skriv estimatet som et tall timer, 0 eller mer.');
  if (d.mode === 'person' && d.assignee_id == null)
    e.assignee_id = T('Velg hvem som er ansvarlig, eller velg Tildel senere.');
  if (d.mode === 'shift' && d.shift_id == null)
    e.shift_id = T('Velg en planlagt vakt, eller velg Tildel senere.');
  if (d.repeat) {
    if (!isDate(d.until)) e.until = T('Velg siste dato for gjentakelsen.');
    else if (d.until < d.start_date) e.until = T('Siste dato kan ikke være før startdatoen.');
    else if (Date.parse(d.until) - Date.parse(d.start_date) > 180 * 86_400_000)
      e.until = T('Gjentakelsen kan vare høyst 180 dager.');
  }
  return e;
}

export function writeOf(d: Draft): TaskWrite {
  return {
    title: d.title.trim(),
    type_id: d.type_id,
    start_date: d.start_date,
    due_date: d.due_date || d.start_date,
    start_time: d.start_time || null,
    estimate_minutes: minutesOf(d.estimate),
    requirement: d.requirement || null,
    assignee_id: d.mode === 'later' ? null : d.assignee_id,
    shift_id: d.mode === 'shift' ? d.shift_id : null,
  };
}

export function draftOf(t: ApiTask): Draft {
  return {
    title: t.title,
    type_id: t.type_id,
    start_date: t.start_date,
    due_date: t.due_date,
    start_time: t.start_time ?? '',
    estimate: hoursOf(t.estimate_minutes),
    requirement: t.requirement ?? '',
    mode: t.shift_id ? 'shift' : t.assignee_id ? 'person' : 'later',
    assignee_id: t.assignee_id,
    shift_id: t.shift_id,
    repeat: '',
    until: '',
    save_as_type: false,
    icon: null,
    color: null,
  };
}

/** How many tasks a repeat makes, the same count as the server (backend taskController occurrences). */
export function occurrenceCount(start: string, cadence: Draft['repeat'], until: string): number {
  if (!cadence || !isDate(start) || !isDate(until) || until < start) return 0;
  const stepDays = cadence === 'daily' ? 1 : cadence === 'weekly' ? 7 : 14;
  return Math.floor((Date.parse(until) - Date.parse(start)) / (stepDays * 86_400_000)) + 1;
}

/* ── the report ───────────────────────────────────────────────────────── */

/** A cell a spreadsheet would run as a formula starts with a quote, then the whole cell is quoted (OWASP CSV injection). */
export function csvCell(v: unknown): string {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[";\n\r]/.test(s) || s !== String(v ?? '') ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The filtered tasks as a semicolon file Excel opens in Norwegian, BOM first so æøå survive. */
export function reportCsv(tasks: ApiTask[], today: string): string {
  const head = [
    'Oppgave',
    'Type',
    'Ansvarlig',
    'Start',
    'Frist',
    'Klokkeslett',
    'Estimat (min)',
    'Dokumentasjon',
    'Status',
    'Utført av',
    'Utført',
    'Unntak, begrunnelse',
  ];
  const rows = tasks.map((t) => [
    t.title,
    t.type?.name ?? '',
    t.assignee_name ?? 'Uten ansvarlig',
    t.start_date,
    t.due_date,
    t.start_time ?? '',
    t.estimate_minutes ?? '',
    t.requirement === 'comment' ? 'Kommentar' : t.requirement === 'measurement' ? 'Måling' : '',
    SHOWN_LABEL[shownOf(t, today)],
    t.done_by_name ?? '',
    t.done_at ? `${oslo(t.done_at)?.date} ${oslo(t.done_at)?.time}` : '',
    t.exception_reason ?? '',
  ]);
  return `﻿${[head, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n')}\r\n`;
}
